import { useState } from 'react';
import { Button, useRecordContext, useNotify, useRefresh, useGetList } from 'react-admin';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemButton,
  CircularProgress,
  Box,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import { useFetchRecordAssociation, useApiMutation } from '../../utils/hooks';
import { Group } from '../../types';

interface AddGroupToMachineButtonProps {
  onSuccess?: () => void;
}

export const AddGroupToMachineButton = ({ onSuccess }: AddGroupToMachineButtonProps) => {
  const [open, setOpen] = useState(false);
  const [selectedGroupIds, setSelectedGroupIds] = useState<number[]>([]);
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Get all groups
  const {
    data: groups,
    isLoading: groupsLoading,
    refetch,
  } = useGetList(
    'groups',
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: 'name', order: 'ASC' },
      filter: {},
    },
    { enabled: open }
  );

  // Get current machine's groups
  const { data: currentGroups, refresh: refreshCurrentGroups } = useFetchRecordAssociation<Group>({
    getEndpoint: (id) => `/api/machines/${id}/groups`,
  });
  const currentGroupIds = currentGroups.map((g) => g.id);

  const { mutate, loading } = useApiMutation();

  const handleOpen = () => {
    setOpen(true);
    refreshCurrentGroups();
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedGroupIds([]);
  };

  const toggleGroupSelection = (groupId: number) => {
    setSelectedGroupIds((prev) =>
      prev.includes(groupId) ? prev.filter((id) => id !== groupId) : [...prev, groupId]
    );
  };

  const handleAddGroups = async () => {
    if (selectedGroupIds.length === 0 || !record?.id) {
      notify('Please select at least one group', { type: 'warning' });
      return;
    }

    const errors: string[] = [];
    let successCount = 0;

    for (const groupId of selectedGroupIds) {
      try {
        await mutate(`/api/machines/${record.id}/groups/${groupId}`);
        successCount++;
      } catch {
        errors.push(`Error associating group ${groupId}`);
      }
    }

    if (successCount > 0) {
      notify(`${successCount} group${successCount > 1 ? 's' : ''} associated successfully`, {
        type: 'success',
      });
      refresh();
      if (onSuccess) {
        onSuccess();
      }
    }

    if (errors.length > 0) {
      notify(`${errors.length} error${errors.length > 1 ? 's' : ''} occurred`, { type: 'error' });
    }

    setSelectedGroupIds([]);
    refetch();
    refreshCurrentGroups();
    // Don't close dialog automatically if there were errors, or maybe do?
    // Original code didn't close dialog explicitly in handleAddGroups but setOpen(false) was not called.
    // Wait, original code didn't call setOpen(false) in handleAddGroups.
    // But usually we want to close it on success.
    if (errors.length === 0) {
      setOpen(false);
    }
  };

  if (!record) return null;

  // Filter groups that are not already associated with this machine
  const availableGroups = groups?.filter((group) => !currentGroupIds.includes(group.id)) || [];

  return (
    <>
      <Button label="Add Group" onClick={handleOpen} startIcon={<AddIcon />} />
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          <Typography variant={isMobile ? 'h6' : 'h5'} component="div">
            Add Groups to Machine {record.serial}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 1, sm: 3 }, py: 2 }}>
          {groupsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : availableGroups.length === 0 ? (
            <Box sx={{ p: 2 }}>
              <Typography color="text.secondary" textAlign="center">
                No groups available to add. This machine is already associated with all groups.
              </Typography>
            </Box>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, px: 1 }}>
                Select groups to associate with this machine (click to select multiple):
              </Typography>
              <List
                sx={{
                  bgcolor: 'background.paper',
                  borderRadius: 1,
                  maxHeight: '400px',
                  overflow: 'auto',
                }}
              >
                {availableGroups.map((group) => (
                  <ListItem
                    key={group.id}
                    disablePadding
                    sx={{
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      '&:last-child': { borderBottom: 'none' },
                    }}
                  >
                    <ListItemButton
                      onClick={() => toggleGroupSelection(group.id)}
                      selected={selectedGroupIds.includes(group.id)}
                      sx={{
                        py: { xs: 1.5, sm: 2 },
                        px: { xs: 1.5, sm: 2.5 },
                        '&.Mui-selected': {
                          backgroundColor:
                            theme.palette.mode === 'dark'
                              ? 'rgba(144, 202, 249, 0.16)'
                              : 'rgba(25, 118, 210, 0.12)',
                          borderLeft: '4px solid',
                          borderLeftColor: 'primary.main',
                          '&:hover': {
                            backgroundColor:
                              theme.palette.mode === 'dark'
                                ? 'rgba(144, 202, 249, 0.24)'
                                : 'rgba(25, 118, 210, 0.18)',
                          },
                        },
                      }}
                    >
                      <Typography
                        variant={isMobile ? 'body2' : 'body1'}
                        fontWeight={selectedGroupIds.includes(group.id) ? 600 : 400}
                      >
                        {group.name}
                      </Typography>
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1, px: 1, display: 'block' }}
              >
                {selectedGroupIds.length} group{selectedGroupIds.length !== 1 ? 's' : ''} selected
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: { xs: 1.5, sm: 2 }, gap: 1 }}>
          <Button
            label="Cancel"
            onClick={handleClose}
            variant="outlined"
            startIcon={<CloseIcon />}
          />
          <Button
            label={`Add ${selectedGroupIds.length > 0 ? selectedGroupIds.length : ''} Group${selectedGroupIds.length !== 1 ? 's' : ''}`}
            onClick={handleAddGroups}
            variant="contained"
            disabled={selectedGroupIds.length === 0 || loading || groupsLoading}
            startIcon={<AddIcon />}
            color="primary"
          />
        </DialogActions>
      </Dialog>
    </>
  );
};
